import React from 'react';
import { Download, Loader2 } from 'lucide-react';
import type { RoastProfilePDF } from '../../roasts/component/RoastProfilePDF';
import { useLanguage } from '../../../features/language/useLanguage';

export const WorkOrderActionsBar = ({
  onCancel,
  isEditMode,
  initialStatus,
  isSubmitting,
  isOutputExceedsInput,
  canSaveAsComplete,
  submitActionRef,
  isCompleted,
  allowSaveRecipe,
  saveAsNewRecipe,
  canDownload,
  downloadData,
  isRoastTypeDirty,
  onSaveRoastType,
  isSavingRoastType,
  isStopwatchInProgress,
  onboardingActive,
  onboardingRoastStep,
}: {
  onCancel: () => void;
  isEditMode: boolean;
  initialStatus?: string;
  isSubmitting: boolean;
  isOutputExceedsInput: boolean;
  canSaveAsComplete: boolean;
  submitActionRef: React.MutableRefObject<'draft' | 'schedule' | 'complete'>;
  isCompleted?: boolean;
  allowSaveRecipe?: boolean;
  saveAsNewRecipe?: boolean;
  canDownload?: boolean;
  downloadData?: React.ComponentProps<typeof RoastProfilePDF> | null;
  isRoastTypeDirty?: boolean;
  onSaveRoastType?: () => void;
  isSavingRoastType?: boolean;
  isStopwatchInProgress?: boolean;
  onboardingActive?: boolean;
  onboardingRoastStep?: boolean;
}) => {
  const { t } = useLanguage();
  const stopwatchHint = isStopwatchInProgress
    ? t('workOrders.actions.stopwatchInProgressHint')
    : undefined;
  // Onboarding step 3 (practice roast): the only outcome the tour accepts is a
  // COMPLETED work order (the step gate is work_orders.status = 'completed').
  // Once the guided draft exists, collapse the choice to a single "Save as
  // Complete" — hiding that button whenever the roast output was blank is what
  // pushed users onto "Save as Scheduled", a path the gate never accepts, so
  // the tour sat still and they re-created the same practice roast twice.
  // Edit mode only: the guide's create stage deliberately ends on "Save as
  // Draft" (the bean/weight/roast-type fields only render after that save), so
  // that button must stay.
  const lockToComplete = !isCompleted && isEditMode && !!onboardingRoastStep;
  const needsOutputWeight = lockToComplete && !canSaveAsComplete;
  const completeHint = needsOutputWeight
    ? t('workOrders.actions.onboardingOutputRequiredHint')
    : stopwatchHint;
  const [isPdfGenerating, setIsPdfGenerating] = React.useState(false);

  const handleDownloadPdf = React.useCallback(async () => {
    if (!downloadData || isPdfGenerating) return;
    setIsPdfGenerating(true);
    try {
      const [{ pdf }, { RoastProfilePDF }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('../../roasts/component/RoastProfilePDF'),
      ]);
      const blob = await pdf(<RoastProfilePDF {...downloadData} />).toBlob();
      const fileName = `${t('common.fileName.roastProfilePrefix')}_${downloadData.roastName.replace(/[^a-z0-9]/gi, '_')}_${downloadData.date}.pdf`;
      const url = URL.createObjectURL(blob);
      
      window.open(url, '_blank');
      
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } finally {
      setIsPdfGenerating(false);
    }
  }, [downloadData, isPdfGenerating, t]);

  return (
    <div className="pt-4 border-t space-y-2">
      {!isCompleted && isStopwatchInProgress && (
        <p
          className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
          data-test-id="wo-stopwatch-in-progress-hint"
        >
          {t('workOrders.actions.stopwatchInProgressHint')}
        </p>
      )}
      {needsOutputWeight && (
        <p
          className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
          data-test-id="wo-onboarding-output-required-hint"
        >
          {t('workOrders.actions.onboardingOutputRequiredHint')}
        </p>
      )}
      <div className="flex justify-end space-x-3">
      <button
        type="button"
        onClick={onCancel}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
      >
        {isCompleted ? t('forms.actions.close') : t('auth.common.cancel')}
      </button>

      {canDownload && downloadData && (
        <button
          type="button"
          onClick={handleDownloadPdf}
          disabled={isPdfGenerating}
          className="inline-flex items-center px-4 py-2 border border-blue-200 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all disabled:opacity-60"
        >
          {isPdfGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          <span>{t('workOrders.actions.downloadProfile')}</span>
        </button>
      )}


      {isCompleted && isRoastTypeDirty && onSaveRoastType && (
        <button
          type="button"
          onClick={onSaveRoastType}
          disabled={isSavingRoastType}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
          data-test-id="wo-save-roast-type-button"
        >
          {isSavingRoastType ? t('forms.actions.saving') : t('workOrders.roastTypeEdit.saveButton')}
        </button>
      )}

      {isCompleted && allowSaveRecipe && (
        <button
          type="submit"
          disabled={isSubmitting || !saveAsNewRecipe}
          onMouseDown={() => {
            submitActionRef.current = 'schedule';
          }}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
          data-test-id="wo-save-recipe-button"
        >
          {isSubmitting ? t('forms.actions.saving') : t('workOrders.actions.saveRecipe')}
        </button>
      )}

      {!isCompleted && (
        <>
          {!isEditMode && (
            <button
              type="submit"
              disabled={isSubmitting || isOutputExceedsInput}
              onMouseDown={() => {
                submitActionRef.current = 'draft';
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
              data-test-id="wo-save-draft-button"
            >
              {isSubmitting ? t('workOrders.actions.savingDraft') : t('workOrders.actions.saveAsDraft')}
            </button>
          )}
          {/* Onboarding step 3: let the user complete a brand-new practice roast
              in one click (the create page handles create→scheduled→completed). */}
          {!isEditMode && onboardingActive && canSaveAsComplete && (
            <button
              type="submit"
              disabled={isSubmitting || isOutputExceedsInput}
              onMouseDown={() => {
                submitActionRef.current = 'complete';
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
              data-test-id="wo-save-complete-button"
            >
              {isSubmitting ? t('forms.actions.saving') : t('workOrders.actions.saveAsComplete')}
            </button>
          )}
          {isEditMode && initialStatus === 'draft' && (
            <>
              {!lockToComplete && (
                <button
                  type="submit"
                  disabled={isSubmitting || isOutputExceedsInput || isStopwatchInProgress}
                  title={stopwatchHint}
                  onMouseDown={() => {
                    submitActionRef.current = 'schedule';
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
                  data-test-id="wo-save-schedule-button"
                >
                  {isSubmitting ? t('forms.actions.saving') : t('workOrders.actions.saveAsScheduled')}
                </button>
              )}
              {(canSaveAsComplete || lockToComplete) && (
                <button
                  type="submit"
                  disabled={isSubmitting || isOutputExceedsInput || isStopwatchInProgress || !canSaveAsComplete}
                  title={completeHint}
                  onMouseDown={() => {
                    submitActionRef.current = 'complete';
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
                  data-test-id="wo-save-complete-button"
                >
                  {isSubmitting ? t('forms.actions.saving') : t('workOrders.actions.saveAsComplete')}
                </button>
              )}
            </>
          )}
          {isEditMode && initialStatus === 'scheduled' && (
            <>
              {!lockToComplete && (
                <button
                  type="submit"
                  disabled={isSubmitting || isOutputExceedsInput || isStopwatchInProgress}
                  title={stopwatchHint}
                  onMouseDown={() => {
                    submitActionRef.current = 'schedule';
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {isSubmitting ? t('forms.actions.saving') : t('forms.actions.save')}
                </button>
              )}
              {(canSaveAsComplete || lockToComplete) && (
                <button
                  type="submit"
                  disabled={isSubmitting || isOutputExceedsInput || isStopwatchInProgress || !canSaveAsComplete}
                  title={completeHint}
                  onMouseDown={() => {
                    submitActionRef.current = 'complete';
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
                  data-test-id="wo-save-complete-button"
                >
                  {isSubmitting ? t('forms.actions.saving') : t('workOrders.actions.saveAsComplete')}
                </button>
              )}
            </>
          )}
          {isEditMode && initialStatus && initialStatus !== 'draft' && initialStatus !== 'scheduled' && initialStatus !== 'completed' && (
            <button
              type="submit"
              disabled={isSubmitting || isOutputExceedsInput || isStopwatchInProgress}
              title={stopwatchHint}
              onMouseDown={() => {
                submitActionRef.current = 'schedule';
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {isSubmitting ? t('forms.actions.saving') : t('forms.actions.save')}
            </button>
          )}
        </>
      )}
      </div>
    </div>
  );
};
